import { type Euler, type Object3D, Vector3 } from 'three';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import type { BVH } from 'three/examples/jsm/loaders/BVHLoader.js';

import { isMhrScene } from '@cartwhl/3d/gltf/utils';

import { PoseManipulationSystem } from '../../lib/PoseManipulationSystem';
import {
  applyKeyPoseSnapshotToScene,
  createKeyPoseSnapshotFromScene,
} from '../keyPoseSnapshots';
import type {
  KeyPoseIkEffectorId,
  KeyPoseEditingPreviewRecord,
  KeyPoseIkTargetPositions,
  KeyPoseRecord,
  KeyPoseVector3Tuple,
} from '../types';
import {
  KEY_POSE_IK_ANKLE_EFFECTOR_IDS,
  KEY_POSE_IK_EFFECTOR_IDS,
  resolveKeyPoseIkEffectorBoneName,
  resolveKeyPoseIkEffectorObject,
  shouldLockEffectorDuringPelvisIkMove,
} from './keyPoseIkConfig';

interface CreateEditableKeyPoseSceneInput {
  actorScene: Object3D | null;
  keyPose: KeyPoseRecord | null;
  sceneRotation?: Euler;
}

interface SolveKeyPoseIkTargetInput extends CreateEditableKeyPoseSceneInput {
  bvh: BVH | null;
  effectorId: KeyPoseIkEffectorId;
  targetWorldPosition: KeyPoseVector3Tuple;
  targetPositions: KeyPoseIkTargetPositions;
  lockFeet: boolean;
  moveWholeCharacter: boolean;
  /** Reused across drag frames to avoid cloning the actor each tick. */
  editableScene?: Object3D | null;
}

const worldToLocalScratch = new Vector3();

export function createEditableKeyPoseScene({
  actorScene,
  keyPose,
  sceneRotation,
}: CreateEditableKeyPoseSceneInput) {
  if (!actorScene || !keyPose) {
    return null;
  }

  const scene = SkeletonUtils.clone(actorScene) as Object3D;
  if (sceneRotation && !isMhrScene(actorScene)) {
    scene.rotation.copy(sceneRotation);
  }

  applyKeyPoseSnapshotToScene(scene, keyPose.skeletonSnapshot);
  return scene;
}

export function getKeyPoseAnkleWorldPositions(
  scene: Object3D,
): KeyPoseIkTargetPositions {
  const positions: KeyPoseIkTargetPositions = {};

  for (const effectorId of KEY_POSE_IK_ANKLE_EFFECTOR_IDS) {
    const position = getKeyPoseIkEffectorWorldPosition(scene, effectorId);
    if (position) {
      positions[effectorId] = position;
    }
  }

  return positions;
}

export function applyKeyPoseAnkleIkConstraints(
  scene: Object3D,
  ankleWorldPositions: KeyPoseIkTargetPositions,
) {
  const poseManipulationSystem = new PoseManipulationSystem(scene);

  for (const effectorId of KEY_POSE_IK_ANKLE_EFFECTOR_IDS) {
    const position = ankleWorldPositions[effectorId];
    if (!position) {
      continue;
    }

    const boneName = resolveKeyPoseIkEffectorBoneName(scene, effectorId);
    if (!boneName) {
      continue;
    }

    poseManipulationSystem.applyConstraint(boneName, vectorFromTuple(position));
  }

  poseManipulationSystem.dispose();
  scene.updateMatrixWorld(true);
}

export function getKeyPoseIkEffectorWorldPosition(
  scene: Object3D,
  effectorId: KeyPoseIkEffectorId,
): KeyPoseVector3Tuple | null {
  const object = resolveKeyPoseIkEffectorObject(scene, effectorId);
  if (!object) {
    return null;
  }

  const position = new Vector3();
  object.getWorldPosition(position);

  return [position.x, position.y, position.z];
}

export function solveKeyPoseIkTarget({
  actorScene,
  bvh,
  keyPose,
  sceneRotation,
  effectorId,
  targetWorldPosition,
  targetPositions,
  lockFeet,
  moveWholeCharacter,
  editableScene,
}: SolveKeyPoseIkTargetInput): KeyPoseEditingPreviewRecord | null {
  if (!bvh || !keyPose) {
    return null;
  }

  const scene =
    editableScene ??
    createEditableKeyPoseScene({
      actorScene,
      keyPose,
      sceneRotation,
    });
  if (!scene) {
    return null;
  }

  if (moveWholeCharacter && effectorId === 'pelvis') {
    moveWholeCharacterToPelvisTarget(scene, targetWorldPosition);
  } else {
    solveEffectorTarget(
      scene,
      effectorId,
      targetWorldPosition,
      targetPositions,
      { lockFeet },
    );
  }

  const snapshot = createKeyPoseSnapshotFromScene({
    scene,
    bvh,
    frame: keyPose.frame,
  });
  if (!snapshot) {
    return null;
  }

  return {
    skeletonSnapshot: snapshot.skeletonSnapshot,
    sourcePose: snapshot.sourcePose,
  };
}

function solveEffectorTarget(
  scene: Object3D,
  effectorId: KeyPoseIkEffectorId,
  targetWorldPosition: KeyPoseVector3Tuple,
  targetPositions: KeyPoseIkTargetPositions,
  options: { lockFeet?: boolean } = {},
) {
  const boneName = resolveKeyPoseIkEffectorBoneName(scene, effectorId);
  if (!boneName) {
    return;
  }

  const supportTargetPositions = getSupportTargetPositions(
    scene,
    targetPositions,
    effectorId,
    options,
  );
  const poseManipulationSystem = new PoseManipulationSystem(scene);
  applyStoredTargets(
    scene,
    poseManipulationSystem,
    supportTargetPositions,
    effectorId,
  );
  if (
    poseManipulationSystem.setActiveSelection({
      handleKey: effectorId,
      boneName,
    })
  ) {
    poseManipulationSystem.setTargetWorldPosition(
      vectorFromTuple(targetWorldPosition),
    );
    poseManipulationSystem.update();
  }
  applyStoredTargets(
    scene,
    poseManipulationSystem,
    supportTargetPositions,
    effectorId,
  );
  poseManipulationSystem.dispose();
}

function getSupportTargetPositions(
  scene: Object3D,
  targetPositions: KeyPoseIkTargetPositions,
  activeEffectorId: KeyPoseIkEffectorId,
  options: { lockFeet?: boolean },
): KeyPoseIkTargetPositions {
  const supportTargetPositions: KeyPoseIkTargetPositions = {
    ...targetPositions,
  };

  if (activeEffectorId === 'pelvis') {
    for (const effectorId of KEY_POSE_IK_EFFECTOR_IDS) {
      if (!shouldLockEffectorDuringPelvisIkMove(effectorId)) {
        delete supportTargetPositions[effectorId];
      }
    }
  }

  if (!options.lockFeet) {
    return supportTargetPositions;
  }

  for (const effectorId of KEY_POSE_IK_ANKLE_EFFECTOR_IDS) {
    if (effectorId === activeEffectorId || supportTargetPositions[effectorId]) {
      continue;
    }

    const object = resolveKeyPoseIkEffectorObject(scene, effectorId);
    if (!object) {
      continue;
    }

    const position = new Vector3();
    object.getWorldPosition(position);
    supportTargetPositions[effectorId] = [position.x, position.y, position.z];
  }

  return supportTargetPositions;
}

function applyStoredTargets(
  scene: Object3D,
  poseManipulationSystem: PoseManipulationSystem,
  targetPositions: KeyPoseIkTargetPositions,
  activeEffectorId: KeyPoseIkEffectorId,
) {
  for (const [effectorId, position] of Object.entries(targetPositions) as [
    KeyPoseIkEffectorId,
    KeyPoseVector3Tuple | undefined,
  ][]) {
    if (!position || effectorId === activeEffectorId) {
      continue;
    }

    const boneName = resolveKeyPoseIkEffectorBoneName(scene, effectorId);
    if (!boneName) {
      continue;
    }

    poseManipulationSystem.applyConstraint(boneName, vectorFromTuple(position));
  }
}

function vectorFromTuple(position: KeyPoseVector3Tuple) {
  return new Vector3(position[0], position[1], position[2]);
}

function moveWholeCharacterToPelvisTarget(
  scene: Object3D,
  targetWorldPosition: KeyPoseVector3Tuple,
) {
  const root = resolveKeyPoseIkEffectorObject(scene, 'pelvis');
  if (!root) {
    return;
  }

  worldToLocalScratch.fromArray(targetWorldPosition);
  if (root.parent) {
    root.parent.worldToLocal(worldToLocalScratch);
  }

  root.position.copy(worldToLocalScratch);
  root.updateMatrixWorld(true);
}
